import { useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { getOrgBrief } from "api";
import useMemoryState from "hooks/useMemoryState";
import { isCurrentMemoryArtifact } from "utils/memoryArtifacts";

// Firestore Timestamp, or a plain { seconds } after a cache round trip.
const toMillis = (timestamp) =>
  timestamp?.toMillis?.() ?? (Number.isFinite(timestamp?.seconds) ? timestamp.seconds * 1000 : null);

// One brief per org snapshot, period and perspective. The server caches it
// and rebuilds it when the snapshot or the user's memories change.
//
// The query key holds the snapshot's last write time and the memory
// revision, so a goal edit or a new nightly step, and a forgotten or edited
// memory, each load a fresh brief. A brief built from older memories is
// hidden, like the Command Center cards, and asked for again.
const useOrgBrief = ({ uid, snapshotDate, snapshotUpdatedAt, period, perspective }) => {
  const { memoryState, loaded } = useMemoryState(uid);
  const revision = loaded ? memoryState?.revision ?? 0 : null;
  const query = useQuery({
    queryKey: ["orgBrief", uid, snapshotDate, toMillis(snapshotUpdatedAt), period, perspective, revision],
    queryFn: () => getOrgBrief({ period, perspective }),
    enabled: Boolean(uid && snapshotDate && period && perspective && loaded),
    staleTime: 12 * 60 * 60 * 1000,
    cacheTime: 12 * 60 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const current = query.data && isCurrentMemoryArtifact(query.data, memoryState, loaded);
  const stale = Boolean(query.data && loaded && !current);

  // A memory can change while the server builds, so the answer can arrive
  // with the previous revision. Ask once more for each such pair; the
  // server rebuilds because its stored revision no longer matches. If the
  // second answer is still old, the row hides instead of loading forever.
  const retried = useRef(null);
  const { refetch, isFetching } = query;
  const stalePair = stale ? `${query.data.memoryRevision}:${revision}` : null;
  useEffect(() => {
    if (!stalePair || isFetching || retried.current === stalePair) return;
    retried.current = stalePair;
    refetch();
  }, [stalePair, isFetching, refetch]);
  const waitingForRetry = Boolean(stalePair && (isFetching || retried.current !== stalePair));

  return {
    ...query,
    data: current ? query.data : undefined,
    isLoading: query.isLoading || !loaded || waitingForRetry,
  };
};

export default useOrgBrief;
